import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Heading,
  Text,
  Stack,
  Icon,
  SimpleGrid,
  Flex,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  FaBuilding,
  FaIndustry,
  FaHeartbeat,
  FaLaptopCode,
  FaBolt,
  FaHandshake,
  FaUtensils,
  FaStore
} from "react-icons/fa";

const MotionBox = motion(Box);
const MotionHeading = motion(Heading);
const MotionStack = motion(Stack);

export default function Businesses() {
  const mainColor = "#000";
  const accentColor = "#c6a664";
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const businesses = t("businesses.items", { returnObjects: true });
  const title = t("businesses.title");
  const description = t("businesses.description");

  const icons = [
    FaBuilding,   // Real Estate
    FaUtensils,   // Food & Trading
    FaIndustry,   // Industrial
    FaBolt,       // Utilities
    FaHeartbeat,  // Healthcare
    FaHandshake,  // Insurance
    FaStore       // Leisure / Retail
  ];

  return (
    <Box
      id="businesses"
      bg="white"
      py={{ base: 12, md: 20 }}
      dir={isArabic ? "rtl" : "ltr"}
      overflow="hidden"
      position="relative"
    >
      <Container maxW="7xl" position="relative" zIndex={1}>
        {/* Section Title */}
        <MotionHeading
          textAlign="center"
          fontSize={{ base: "2xl", md: "4xl" }}
          color={mainColor}
          mb={4}
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          {title}
        </MotionHeading>

        <Text
          textAlign="center"
          color="gray.600"
          fontSize={{ base: "md", md: "lg" }}
          maxW="3xl"
          mx="auto"
          mb={12}
        >
          {description}
        </Text>

        {/* Business Sectors Grid */}
        <Flex
          flexWrap="wrap"
          justify="center"
          gap={8}
        >
          {businesses.map((sector, index) => (
            <MotionStack
              key={index}
              align="center"
              p={6}
              spacing={4}
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="lg"
              textAlign="center"
              boxShadow="md"
              flex={{ base: "1 1 100%", sm: "1 1 calc(50% - 2rem)", md: "1 1 calc(25% - 2rem)", lg: "1 1 calc(22% - 2rem)" }}
              maxW={{ base: "100%", sm: "48%", md: "23%" }}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              _hover={{
                boxShadow: "xl",
                transform: "translateY(-6px)",
                transition: "0.3s ease",
                borderColor: accentColor,
              }}
            >
              <Box
                bg={mainColor}
                w="60px"
                h="60px"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                mb={3}
              >
                <Icon as={icons[index % icons.length]} color={accentColor} boxSize={6} />
              </Box>

              <Heading
                fontSize="lg"
                color={mainColor}
                fontWeight="semibold"
                whiteSpace="nowrap"
              >
                {sector.title}
              </Heading>
            </MotionStack>
          ))}
        </Flex>
      </Container>

      {/* Decorative gold line */}
      <Box
        position="absolute"
        bottom={0}
        left="50%"
        transform="translateX(-50%)"
        w="60%"
        h="2px"
        bgGradient={`linear(to-r, transparent, ${accentColor}, transparent)`}
        opacity={0.4}
      />
    </Box>
  );
}
